/**
 * Pricing helpers.
 *
 * Pricing math lives in the backend (`billing/services.py::compute_price_breakdown`)
 * and is surfaced via `/billing/active-pricing`. The frontend NEVER hardcodes
 * tier tables or annual discounts — UI components hydrate from the backend
 * and only do cheap arithmetic / lookup.
 *
 * This module only carries:
 *  - `PRO_MAX_SEATS` — the cap; matches `settings.BILLING_PRO_MAX_SEATS`.
 *  - `formatCents(cents, locale)` — pure locale-aware euro formatting.
 *  - `totalAtSeats(totals, seats)` — picks the total at a given seat count
 *    from the `totals.monthly[seats-1]` array shape exposed by the backend.
 *  - `perSeatCents(totals, seats)` — el precio por local del número grande.
 *  - `anchorPerSeatCents(pricing, period, seats)` — el precio tachado, en la
 *    misma unidad. Regla única para las 3 superficies de precio.
 *  - `savingsBreakdown(pricing, period, seats)` — el desglose "precio completo
 *    → descuentos → tu precio" del recuadro de ahorro.
 *  - `formatEuros(cents, locale, decimals)` — formato con decimales fijos.
 *  - `formatPrice(cents, locale, decimals, displayCurrency)` — lo mismo, pero en
 *    la divisa local del visitante cuando el backend la manda (Adaptive Pricing).
 */

export const PRO_MAX_SEATS = 30;

/**
 * Acota un número de locales al rango que cubre la tabla del backend.
 *
 * Lo comparten `totalAtSeats` y `perSeatCents` a propósito: el total se busca
 * en la tabla acotando, así que dividirlo entre un `seats` SIN acotar daría un
 * precio por local que no existe. No es teórico — `?seats=999` en la URL llega
 * hasta aquí desde `UpgradeCard`.
 *
 * @param {number} seats
 * @param {number} max   longitud de la tabla (normalmente PRO_MAX_SEATS)
 */
function clampSeats(seats, max) {
  const n = Math.floor(Number(seats) || 0);
  return Math.max(1, Math.min(max || PRO_MAX_SEATS, n));
}

/**
 * Pick the total at a given seat count from the backend `totals` array.
 *
 * @param {number[]} totalsForPeriod   Array of cent totals indexed by seats-1.
 * @param {number} seats               1..PRO_MAX_SEATS (clamped here).
 * @returns {number} total cents, or 0 if seats out of range / array missing.
 */
export function totalAtSeats(totalsForPeriod, seats) {
  if (!Array.isArray(totalsForPeriod) || totalsForPeriod.length === 0) return 0;
  return Number(totalsForPeriod[clampSeats(seats, totalsForPeriod.length) - 1]) || 0;
}

/**
 * Precio POR LOCAL en la unidad del periodo elegido — el número grande de las
 * tres superficies de precio (Pricing, Landing y la card de la app).
 *
 * En mensual devuelve €/local/mes; en anual, €/local/año. NO mensualiza el
 * anual: cada modo del toggle muestra su propia columna de la tarifa publicada,
 * que es lo que firmó el CTO.
 *
 * Se deriva del total del backend en vez de leer una tabla de tramos. En la
 * tarifa por volumen (v3) `total = unidad × locales`, así que dividir devuelve
 * el precio de la tabla EXACTO y sin residuo de redondeo en ninguno de los dos
 * periodos. En las familias graduated (launch, v2) no existe un precio unitario
 * único y esto es la MEDIA por local — que es lo que el cliente paga por local
 * de todas formas, pero no cuadra a un número redondo.
 *
 * @param {number[]} totalsForPeriod  `totals.monthly` o `totals.annual`
 * @param {number} seats
 * @returns {number} céntimos por local y periodo
 */
export function perSeatCents(totalsForPeriod, seats) {
  if (!Array.isArray(totalsForPeriod) || totalsForPeriod.length === 0) return 0;
  // Mismo acotado que el de la búsqueda del total: si no, con seats fuera de
  // rango se dividiría el total del tope entre el seats crudo.
  const n = clampSeats(seats, totalsForPeriod.length);
  return Math.round(totalAtSeats(totalsForPeriod, n) / n);
}

/**
 * Precio POR LOCAL tachado — el "antes" contra el que se lee el número grande,
 * en la MISMA unidad que él (€/local/año en anual, €/local/mes en mensual).
 *
 * Cada periodo se compara contra lo que el cliente pagaría por no elegirlo:
 *
 *  - Anual  → la tarifa MENSUAL de esta misma familia × 12. Es literalmente lo
 *    que costaría el mismo año pagando mes a mes, así que el tachado enseña el
 *    descuento anual sin repetir el porcentaje del badge. No se usa `anchor`:
 *    mezclar el ahorro anual con el cambio de tarifa inflaría el tachado.
 *  - Mensual → la tarifa ANTERIOR (`pricing.anchor`, v2), que el backend manda
 *    ya calculada. Sin bloque `anchor` no hay nada que tachar.
 *
 * Devuelve 0 —falsy, "no tachar"— cuando el ancla no es MAYOR que el precio
 * actual. Esa guarda no es teórica: en cuanto el ancla deje de ser más cara que
 * la tarifa vigente en algún conteo, tacharla anunciaría una subida de precio.
 *
 * @param {{totals: {monthly: number[], annual: number[]},
 *          anchor?: {totals: {monthly: number[]}} | null}} pricing
 * @param {'monthly'|'annual'} period
 * @param {number} seats
 * @returns {number} céntimos por local y periodo, o 0 si no hay tachado
 */
export function anchorPerSeatCents(pricing, period, seats) {
  if (!pricing || !pricing.totals) return 0;
  const current = perSeatCents(pricing.totals[period], seats);
  const anchor = period === 'annual'
    ? perSeatCents(pricing.totals.monthly, seats) * 12
    : perSeatCents(pricing.anchor?.totals?.monthly, seats);
  return anchor > current ? anchor : 0;
}

/**
 * Desglose del recuadro de ahorro: de qué precio partimos, qué descuentos se
 * aplican y en cuál acabamos. Todo en céntimos ENTEROS y en la unidad del
 * periodo (€/ubicación/mes en mensual, €/ubicación/año en anual).
 *
 * El "precio completo" es NUESTRO precio a 1 ubicación, no la tarifa retirada
 * (v2). Es decir, el recuadro cuenta el descuento por VOLUMEN —y, en anual, el
 * descuento por pagar por adelantado— y no un cambio de tarifa. Por eso
 * `anchorPerSeatCents` ya no lo usa ninguna de las 3 superficies: se conserva
 * porque `STRIPE_ANCHOR_PRICE_FAMILY` sigue siendo una palanca de runtime.
 *
 * Dos garantías, ambas cubiertas por tests:
 *
 *  1. ADITIVIDAD EXACTA: `full - rowAnnual - rowVolume === final`, en enteros y
 *     sin deriva de redondeo. Sale por construcción, porque
 *     `rowAnnual + rowVolume = M[0]×12 − final = full − final`.
 *  2. `savings` NO se calcula como `(full − final) × n`. Eso compondría el
 *     `Math.round` de `perSeatCents` hasta n/2 céntimos: exacto en la tarifa por
 *     volumen (v3), donde `total = unidad × n`, pero desviado en las graduated
 *     (launch, v2), donde `perSeatCents` es una media. Se resta del total real.
 *
 * Filas a 0 se ocultan (en mensual no hay descuento anual; a 1 ubicación no hay
 * descuento por volumen). `visible: false` ⇒ no se pinta el recuadro entero,
 * que es el caso de 1 ubicación en mensual: ahí no hay nada que descontar.
 *
 * @param {{totals: {monthly: number[], annual: number[]}}} pricing
 * @param {'monthly'|'annual'} period
 * @param {number} seats
 * @returns {{full: number, rowAnnual: number, rowVolume: number,
 *            final: number, savings: number, pct: number, visible: boolean}}
 */
export function savingsBreakdown(pricing, period, seats) {
  const empty = {
    full: 0, rowAnnual: 0, rowVolume: 0, final: 0,
    savings: 0, pct: 0, visible: false,
  };
  if (!pricing || !pricing.totals) return empty;

  const monthly = pricing.totals.monthly;
  const annual = pricing.totals.annual;
  const periodTotals = pricing.totals[period];
  if (!Array.isArray(monthly) || monthly.length === 0) return empty;
  if (!Array.isArray(periodTotals) || periodTotals.length === 0) return empty;

  const isAnnual = period === 'annual';
  // `totals[0]` es el total con 1 ubicación, que con 1 ubicación coincide con el
  // precio unitario. En anual el punto de partida es pagar mes a mes ese año.
  const oneSeatMonthly = Number(monthly[0]) || 0;
  const oneSeatAnnual = Array.isArray(annual) ? Number(annual[0]) || 0 : 0;
  const full = isAnnual ? oneSeatMonthly * 12 : oneSeatMonthly;
  if (full <= 0) return empty;

  const final = perSeatCents(periodTotals, seats);
  const rowAnnual = isAnnual ? Math.max(0, full - oneSeatAnnual) : 0;
  // `Math.max(0, …)` para que una tabla futura no monótona no pinte un
  // "descuento" negativo.
  const rowVolume = Math.max(0, (isAnnual ? oneSeatAnnual : oneSeatMonthly) - final);

  // Acotado igual que la búsqueda del total: con `?seats=999`, `full × 999`
  // contra el total del tope anunciaría un ahorro inventado.
  const n = clampSeats(seats, periodTotals.length);
  const savings = full * n - totalAtSeats(periodTotals, n);

  return {
    full,
    rowAnnual,
    rowVolume,
    final,
    savings,
    pct: Math.round((1 - final / full) * 100),
    visible: savings > 0,
  };
}

/**
 * Desglose del CARRITO entero —N locales, no uno—, que es lo que enseña la card
 * de suscripción: «3 locales × 29,90 €», el descuento anual, el de volumen y el
 * total. `savingsBreakdown` cuenta lo mismo pero POR LOCAL, que es lo que pintaba
 * la card anterior.
 *
 * Todo en céntimos enteros y en la unidad del periodo. Aditivo por construcción:
 * el descuento por volumen se obtiene restando del TOTAL REAL del backend, así
 * que `gross - annualDiscount - volumeDiscount === total` siempre, también en
 * las familias graduated, donde el precio por local es una media.
 *
 * @param {{totals: {monthly: number[], annual: number[]}}} pricing
 * @param {'monthly'|'annual'} period
 * @param {number} seats
 * @returns {{seats: number, listUnit: number, unit: number, gross: number,
 *            annualDiscount: number, volumeDiscount: number, total: number,
 *            savings: number, pct: number}}
 */
export function cartBreakdown(pricing, period, seats) {
  const monthly = pricing?.totals?.monthly;
  const annual = pricing?.totals?.annual;
  const periodTotals = pricing?.totals?.[period];
  const empty = {
    seats: 1, listUnit: 0, unit: 0, gross: 0, annualDiscount: 0,
    volumeDiscount: 0, total: 0, savings: 0, pct: 0,
  };
  if (!Array.isArray(monthly) || !monthly.length) return empty;
  if (!Array.isArray(periodTotals) || !periodTotals.length) return empty;

  const isAnnual = period === 'annual';
  const n = clampSeats(seats, periodTotals.length);
  const oneSeatMonthly = Number(monthly[0]) || 0;
  const oneSeatAnnual = Array.isArray(annual) ? Number(annual[0]) || 0 : 0;
  // Precio de lista por local y periodo: en anual, el mismo año pagado mes a mes.
  const listUnit = isAnnual ? oneSeatMonthly * 12 : oneSeatMonthly;
  const baseUnit = isAnnual ? oneSeatAnnual : oneSeatMonthly;

  const total = totalAtSeats(periodTotals, n);
  const gross = listUnit * n;
  const annualDiscount = isAnnual ? Math.max(0, (listUnit - baseUnit) * n) : 0;
  const volumeDiscount = Math.max(0, gross - annualDiscount - total);
  const savings = Math.max(0, gross - total);

  return {
    seats: n,
    listUnit,
    unit: perSeatCents(periodTotals, n),
    gross,
    annualDiscount,
    volumeDiscount,
    total,
    savings,
    pct: gross > 0 ? Math.round((savings / gross) * 100) : 0,
  };
}

/**
 * Format euros from cents respecting the active locale. Whole-euro values
 * are rendered without decimals (€30, not €30.00); fractional ones keep
 * two decimals. Symbol placement follows the locale convention via
 * Intl.NumberFormat.
 */
export function formatCents(cents, locale = 'es-ES') {
  const euros = (Number(cents) || 0) / 100;
  const opts = {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: Number.isInteger(euros) ? 0 : 2,
    maximumFractionDigits: 2,
  };
  return new Intl.NumberFormat(locale, opts).format(euros);
}

/**
 * Euros con un número FIJO de decimales, decidido por el llamador.
 *
 * `formatCents` decide los decimales por el valor (30 € pero 17,40 €), que es lo
 * correcto para un importe suelto pero incoherente dentro de una columna: en el
 * desglose, "24,90 €" encima de "168 €" baila. La card fija los decimales por
 * sitio igual que el diseño: 2 en mensual y en las filas del desglose, 0 en el
 * número grande y el total anuales.
 *
 * El símbolo va DETRÁS, que es la convención de es-ES y la del diseño (las
 * páginas públicas lo anteponían: `€240,00`).
 *
 * @param {number} cents
 * @param {string} locale
 * @param {number} decimals  0 | 2
 */
export function formatEuros(cents, locale = 'es-ES', decimals = 2) {
  const euros = (Number(cents) || 0) / 100;
  return `${euros.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} €`;
}

/**
 * ¿Hay que pintar en otra divisa? Solo si el backend mandó una que no es EUR y
 * con un tipo utilizable. Cualquier otra cosa —sin bloque, tipo 0, NaN— es EUR:
 * mejor el precio en euros que un «≈ 0 COP».
 *
 * @param {{code?: string, rate?: number, approx?: boolean} | null | undefined} displayCurrency
 */
function isLocalCurrency(displayCurrency) {
  const code = String(displayCurrency?.code || 'eur').toLowerCase();
  const rate = Number(displayCurrency?.rate);
  return code !== 'eur' && Number.isFinite(rate) && rate > 0;
}

/**
 * Un importe de la tarifa en la divisa que enseñará el Checkout a este visitante.
 *
 * Los céntimos siguen siendo de EUR —toda la matemática de precios se hace en
 * euros— y aquí solo se convierten para pintarlos. `displayCurrency` es el
 * bloque de `/billing/active-pricing` (ver `billing/local_currency.py`): la
 * divisa de Adaptive Pricing para el país del visitante y cuántas unidades vale
 * 1 EUR.
 *
 *  - En EUR la salida es EXACTAMENTE la de `formatEuros`: nada cambia para
 *    quien ve euros.
 *  - En otra divisa es aproximado (Stripe fija el importe final al pagar), así
 *    que va con «≈» y sin fingir precisión: a partir de 100 unidades, sin
 *    decimales («≈ 112.560 COP»); por debajo, con los de la divisa («≈ 34,01 US$»).
 *
 * @param {number} cents   céntimos de EUR
 * @param {string} locale
 * @param {number} decimals  decimales si el importe se pinta en EUR (0 | 2)
 * @param {{code?: string, rate?: number, approx?: boolean} | null} [displayCurrency]
 */
export function formatPrice(cents, locale = 'es-ES', decimals = 2, displayCurrency = null) {
  if (!isLocalCurrency(displayCurrency)) return formatEuros(cents, locale, decimals);

  const amount = ((Number(cents) || 0) / 100) * Number(displayCurrency.rate);
  const currency = String(displayCurrency.code).toUpperCase();
  const opts = { style: 'currency', currency };
  if (Math.abs(amount) >= 100) {
    opts.minimumFractionDigits = 0;
    opts.maximumFractionDigits = 0;
  }
  let text;
  try {
    text = new Intl.NumberFormat(locale, opts).format(amount);
  } catch {
    // Un código que el navegador no conoce: mejor euros que una excepción que
    // tumbe la card de precios.
    return formatEuros(cents, locale, decimals);
  }
  return displayCurrency.approx === false ? text : `≈ ${text}`;
}

/** Código ISO (mayúsculas) de la divisa local, o `null` si se pinta en EUR. */
export function localCurrencyCode(displayCurrency) {
  return isLocalCurrency(displayCurrency) ? String(displayCurrency.code).toUpperCase() : null;
}
